import {
    assertUnreachable,
    getSearchItemTypeFromResultKey,
    SearchItemType,
    type SavedChartSearchResult,
    type SearchResults,
} from '@lightdash/common';
import {
    Icon123,
    IconAbc,
    IconAppWindow,
    IconBrowser,
    IconFolder,
    IconLayoutDashboard,
    IconLayoutNavbarInactive,
    IconSettings,
    IconTable,
} from '@tabler/icons-react';
import { getChartIcon } from '../../../components/common/ResourceIcon/utils';
import { type SearchItem } from '../types/searchItem';
import { type SearchResultMap } from '../types/searchResultMap';

export const getOmnibarItemColor = (itemType: SearchItemType) => {
    switch (itemType) {
        case SearchItemType.FIELD:
            return 'ldGray.7';
        case SearchItemType.DASHBOARD_TAB:
        case SearchItemType.DASHBOARD:
            return 'green.6';
        case SearchItemType.CHART:
            return 'blue.6';
        case SearchItemType.SPACE:
            return 'violet.6';
        case SearchItemType.TABLE:
            return 'cyan.8';
        case SearchItemType.PAGE:
            return 'ldGray.7';
        case SearchItemType.SQL_CHART:
            return 'blue.7';
        case SearchItemType.DATA_APP:
            return 'orange.6';
        case SearchItemType.SETTINGS:
            return 'ldGray.7';
        default:
            return assertUnreachable(
                itemType,
                `Unknown search item type: ${itemType}`,
            );
    }
};

export const getOmnibarItemIcon = (item: SearchItem) => {
    if (item.icon) {
        return item.icon;
    }
    switch (item.type) {
        case SearchItemType.FIELD:
            if (item.typeLabel?.toLowerCase() === 'dimension') {
                return IconAbc;
            } else {
                return Icon123;
            }
        case SearchItemType.DASHBOARD:
            return IconLayoutDashboard;
        case SearchItemType.CHART:
        case SearchItemType.SQL_CHART:
            return getChartIcon(
                // FIXME: typing is loose here
                (item.item as SavedChartSearchResult)?.chartType,
            );
        case SearchItemType.SPACE:
            return IconFolder;
        case SearchItemType.TABLE:
            return IconTable;
        case SearchItemType.PAGE:
            return IconBrowser;
        case SearchItemType.DASHBOARD_TAB:
            return IconLayoutNavbarInactive;
        case SearchItemType.DATA_APP:
            return IconAppWindow;
        case SearchItemType.SETTINGS:
            return IconSettings;
        default:
            return assertUnreachable(
                item.type,
                `Unknown search item type: ${item.type}`,
            );
    }
};

export const itemHasValidationError = (searchItem: SearchItem) =>
    searchItem.item &&
    ['dashboard', 'saved_chart', 'table'].includes(searchItem.type) &&
    'validationErrors' in searchItem.item &&
    searchItem.item.validationErrors?.length > 0;

export const itemHasVerification = (searchItem: SearchItem) =>
    Boolean(searchItem.recentContent?.verification) ||
    (searchItem.item &&
        'verification' in searchItem.item &&
        searchItem.item.verification !== null &&
        searchItem.item.verification !== undefined);

export const getSearchResultsGroupsSorted = (results: SearchResultMap) =>
    Object.entries(results)
        .map((items) => {
            return [
                getSearchItemTypeFromResultKey(items[0] as keyof SearchResults),
                items[1],
            ] as [SearchItemType, SearchItem[]];
        })
        .filter(([_type, items]) => items.length > 0)
        .sort(
            ([_a, itemsA], [_b, itemsB]) =>
                (itemsB[0].searchRank ?? 0) - (itemsA[0].searchRank ?? 0),
        );
